import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type PlanTier = 'trial' | 'professional' | 'enterprise';

type OrgBilling = {
  id: string;
  name: string;
  planTier: PlanTier;
  stripeSubscriptionStatus: string | null;
  trialEndsAt: string | null;
  createdAt: string;
};

type Member = {
  userId: string;
  role: string;
  invitedAt: string | null;
  acceptedAt: string | null;
  email: string | null;
  name: string | null;
};

const TIER_BADGE: Record<PlanTier, string> = {
  trial: 'bg-amber-100 text-amber-700',
  professional: 'bg-blue-100 text-blue-700',
  enterprise: 'bg-purple-100 text-purple-700',
};

const TIER_LABEL: Record<PlanTier, string> = {
  trial: 'Trial',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

const ROLE_BADGE: Record<string, string> = {
  owner: 'bg-amber-100 text-amber-700',
  analyst: 'bg-slate-100 text-slate-600',
  reviewer: 'bg-blue-50 text-blue-600',
  consultant: 'bg-teal-50 text-teal-700',
};

export function AdminOrgBillingRoute() {
  const { orgId } = useParams<{ orgId: string }>();
  const getToken = useSessionTokenGetter();

  const [org, setOrg] = useState<OrgBilling | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [planTier, setPlanTier] = useState<PlanTier>('trial');
  const [subStatus, setSubStatus] = useState<string>('');
  const [trialEndsAt, setTrialEndsAt] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/admin/orgs/${orgId}/billing`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error((j as { error?: string }).error ?? `${res.status}`);
        }
        const data = (await res.json()) as { org: OrgBilling; members: Member[] };
        if (!cancelled) {
          setOrg(data.org);
          setMembers(data.members);
          setPlanTier(data.org.planTier);
          setSubStatus(data.org.stripeSubscriptionStatus ?? '');
          setTrialEndsAt(
            data.org.trialEndsAt ? new Date(data.org.trialEndsAt).toISOString().slice(0, 10) : '',
          );
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, orgId]);

  async function handleSave() {
    if (!orgId) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/admin/orgs/${orgId}/billing`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planTier,
          stripeSubscriptionStatus: subStatus || null,
          trialEndsAt: trialEndsAt || null,
        }),
      });
      const j = await res.json();
      if (!res.ok) {
        setSaveResult(`Error: ${(j as { error?: string }).error ?? res.status}`);
      } else {
        setOrg((j as { org: OrgBilling }).org);
        setSaveResult('Saved');
      }
    } catch (e) {
      setSaveResult(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="h-6 w-40 animate-pulse rounded bg-slate-200" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 space-y-8">
      {/* Breadcrumbs */}
      <div>
        <div className="flex items-center gap-1 text-xs text-slate-400">
          <Link to="/admin" className="hover:underline">
            All organizations
          </Link>
          <span>›</span>
          <Link to={`/admin/orgs/${orgId}`} className="hover:underline">
            {org?.name ?? orgId}
          </Link>
          <span>›</span>
          <span className="text-slate-600">Billing</span>
        </div>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">
          Billing — {org?.name ?? orgId}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Manual overrides bypass Stripe. Changes are audit-logged.
        </p>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Current state */}
      {org && (
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="mb-3 text-sm font-semibold text-slate-700">Current state</h2>
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-400">Plan</p>
              <span
                className={`mt-1 inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${TIER_BADGE[org.planTier]}`}
              >
                {TIER_LABEL[org.planTier]}
              </span>
            </div>
            <div>
              <p className="text-xs text-slate-400">Stripe status</p>
              <p className="mt-1 font-medium text-slate-700">
                {org.stripeSubscriptionStatus ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Trial ends</p>
              <p className="mt-1 font-medium text-slate-700">
                {org.trialEndsAt ? new Date(org.trialEndsAt).toLocaleDateString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Customer since</p>
              <p className="mt-1 font-medium text-slate-700">
                {new Date(org.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Override form */}
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <h2 className="mb-4 text-sm font-semibold text-slate-700">Manual override</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="plan-tier">
              Plan tier
            </label>
            <select
              id="plan-tier"
              value={planTier}
              onChange={(e) => setPlanTier(e.target.value as PlanTier)}
              className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-develio-teal"
            >
              <option value="trial">Trial</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="sub-status">
              Subscription status
            </label>
            <select
              id="sub-status"
              value={subStatus}
              onChange={(e) => setSubStatus(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-develio-teal"
            >
              <option value="">— none —</option>
              <option value="active">active</option>
              <option value="trialing">trialing</option>
              <option value="past_due">past_due</option>
              <option value="canceled">canceled</option>
              <option value="paused">paused</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="trial-ends">
              Trial ends (date)
            </label>
            <input
              id="trial-ends"
              type="date"
              value={trialEndsAt}
              onChange={(e) => setTrialEndsAt(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-develio-teal"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {saveResult && (
            <span
              className={`text-xs font-medium ${saveResult.startsWith('Error') ? 'text-red-600' : 'text-green-700'}`}
            >
              {saveResult}
            </span>
          )}
        </div>
      </div>

      {/* Members */}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-700">Members ({members.length})</h2>
        </div>
        {members.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-400">No members.</p>
        ) : (
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">User</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">Role</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {members.map((m) => (
                <tr key={m.userId} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-slate-900">{m.name ?? '—'}</p>
                    <p className="text-xs text-slate-400">{m.email ?? m.userId}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${ROLE_BADGE[m.role] ?? 'bg-slate-100 text-slate-600'}`}
                    >
                      {m.role}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-400">
                    {m.acceptedAt
                      ? new Date(m.acceptedAt).toLocaleDateString()
                      : m.invitedAt
                        ? `Invited ${new Date(m.invitedAt).toLocaleDateString()}`
                        : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Admin access note */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
        <p className="font-semibold mb-1">Adding admin users</p>
        <p>
          Admin access is controlled via the <code className="font-mono">ADMIN_USER_IDS</code>{' '}
          environment variable (comma-separated Clerk user IDs). Update it in your deployment
          environment and restart the API to grant or revoke admin access.
        </p>
      </div>
    </div>
  );
}
